import React, { useState, useEffect } from 'react'
import { Box, Typography, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper, CircularProgress, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import FilterListIcon from "@mui/icons-material/FilterList";
import { FaFolder, FaCog, FaCheckSquare, FaExclamationTriangle, FaTimesCircle } from "react-icons/fa";
import { CommonFilterSidebar, CommonSelect } from "../common";
import { primaryBlueColor } from "../common/commonButtonStyles";
import GenericDonutChart from "../Dashboard/GenericDonutChart";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import instance from "../../redux/api";
import { GetWorkflowDashboardSummary, GetAgingAnalysis, GetWorkflowTypeBreakdown, GetWorkflowOverview, GetTeamActivity, GetPendingWorkflowDetails, GetUsers } from "../../Services/api";
import { toast } from "react-toastify";
import { format } from "date-fns";
import "../common/commonPageStyles.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const WorkflowDashboardContainer = () => {
  const toggle = useSelector((state) => state.toggleSideMenu);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);

  // Workflow summary metrics
  const [totalWorkflows, setTotalWorkflows] = useState(0);
  const [inProcess, setInProcess] = useState(0);
  const [completed, setCompleted] = useState(0);
  const [overdue, setOverdue] = useState(0);
  const [rejected, setRejected] = useState(0);

  // Filter state
  const [workflowName, setWorkflowName] = useState("");
  const [dateReceivedFrom, setDateReceivedFrom] = useState("");
  const [dateReceivedTo, setDateReceivedTo] = useState("");
  const [initiatedBy, setInitiatedBy] = useState("");
  const [status, setStatus] = useState("");
  const [transactionId, setTransactionId] = useState("");
  
  // Filter search states for searchable dropdowns
  const [workflowNameSearch, setWorkflowNameSearch] = useState("");
  const [initiatedBySearch, setInitiatedBySearch] = useState("");
  const [transactionIdSearch, setTransactionIdSearch] = useState("");
  const [selectedWorkflows, setSelectedWorkflows] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);
  
  // Lists for dropdowns
  const [workflowList, setWorkflowList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [transactionIdList, setTransactionIdList] = useState([]);
  
  // Loading states for each section
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isLoadingAgingAnalysis, setIsLoadingAgingAnalysis] = useState(false);
  const [isLoadingTypeBreakdown, setIsLoadingTypeBreakdown] = useState(false);
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);
  const [isLoadingTeamActivity, setIsLoadingTeamActivity] = useState(false);
  const [isLoadingPendingDetails, setIsLoadingPendingDetails] = useState(false);

  // Build query parameters helper
  const buildQueryParams = () => {
    const queryParams = new URLSearchParams();
    
    // Use selected workflow from dropdown if available, otherwise use text input
    if (selectedWorkflows.length > 0) {
      queryParams.append('workflow_name', selectedWorkflows[0]);
    } else if (workflowName) {
      queryParams.append('workflow_name', workflowName);
    }
    
    if (dateReceivedFrom) {
      // Format date to YYYY-MM-DD format for backend
      const formattedDate = dateReceivedFrom instanceof Date 
        ? format(dateReceivedFrom, 'yyyy-MM-dd')
        : dateReceivedFrom;
      queryParams.append('date_from', formattedDate);
    }
    if (dateReceivedTo) {
      // Format date to YYYY-MM-DD format for backend
      const formattedDate = dateReceivedTo instanceof Date 
        ? format(dateReceivedTo, 'yyyy-MM-dd')
        : dateReceivedTo;
      queryParams.append('date_to', formattedDate);
    }
    
    // Use selected user from dropdown if available
    if (selectedUsers.length > 0) {
      queryParams.append('initiated_by', selectedUsers[0]);
    } else if (initiatedBy) {
      queryParams.append('initiated_by', initiatedBy);
    }
    
    if (status) {
      queryParams.append('status', status);
    }
    
    // Use selected transaction ID from dropdown if available
    if (selectedTransactionIds.length > 0) {
      queryParams.append('transaction_id', selectedTransactionIds[0]);
    } else if (transactionId) {
      queryParams.append('transaction_id', transactionId);
    }
    
    return queryParams.toString();
  };

  // Fetch workflow dashboard summary data
  const fetchWorkflowDashboardSummary = async () => {
    try {
      setIsLoadingSummary(true);
      
      const queryString = buildQueryParams();
      const url = queryString 
        ? `${GetWorkflowDashboardSummary}?${queryString}`
        : GetWorkflowDashboardSummary;
      
      const response = await instance.get(url);
      
      // Check if response is successful and has data
      if (response && response.status === 200 && response.data) {
        const data = response.data;
        console.log('Workflow Dashboard Summary Response:', data);
        console.log('Summary URL:', url);
        console.log('Summary Query Params:', queryString);
        // Handle both direct data and nested data structure
        const summaryData = data.data || data;
        setTotalWorkflows(summaryData.totalWorkflows ?? 0);
        setInProcess(summaryData.inProcess ?? 0);
        setCompleted(summaryData.completed ?? 0);
        setOverdue(summaryData.overdue ?? 0);
        setRejected(summaryData.rejected ?? 0);
        console.log('Set Summary Values:', {
          totalWorkflows: summaryData.totalWorkflows ?? 0,
          inProcess: summaryData.inProcess ?? 0,
          completed: summaryData.completed ?? 0,
          overdue: summaryData.overdue ?? 0,
          rejected: summaryData.rejected ?? 0
        });
      } else {
        console.error('Error fetching workflow dashboard summary - Invalid response:', response);
        setTotalWorkflows(0);
        setInProcess(0);
        setCompleted(0);
        setOverdue(0);
        setRejected(0);
      }
    } catch (error) {
      console.error('Error fetching workflow dashboard summary:', error);
      console.error('Error details:', error.response?.data || error.message);
      setTotalWorkflows(0);
      setInProcess(0);
      setCompleted(0);
      setOverdue(0);
      setRejected(0);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  // Fetch aging analysis data
  const fetchAgingAnalysis = async () => {
    try {
      setIsLoadingAgingAnalysis(true);
      const queryString = buildQueryParams();
      const url = queryString 
        ? `${GetAgingAnalysis}?${queryString}`
        : GetAgingAnalysis;
      
      const response = await instance.get(url);
      
      if (response && response.status === 200 && response.data) {
        const data = response.data;
        setAgingAnalysisData({
          labels: data.labels || [],
          datasets: [
            {
              label: "Count",
              data: data.counts || [],
              backgroundColor: primaryBlueColor,
              borderColor: primaryBlueColor,
              borderWidth: 1,
            },
          ],
        });
      } else {
        console.error('Error fetching aging analysis:', response);
        setAgingAnalysisData({
          labels: [],
          datasets: [
            {
              label: "Count",
              data: [],
              backgroundColor: primaryBlueColor,
              borderColor: primaryBlueColor,
              borderWidth: 1,
            },
          ],
        });
      }
    } catch (error) {
      console.error('Error fetching aging analysis:', error);
      setAgingAnalysisData({
        labels: [],
        datasets: [
          {
            label: "Count",
            data: [],
            backgroundColor: primaryBlueColor,
            borderColor: primaryBlueColor,
            borderWidth: 1,
          },
        ],
      });
    } finally {
      setIsLoadingAgingAnalysis(false);
    }
  };

  // Fetch workflow type breakdown data
  const fetchWorkflowTypeBreakdown = async () => {
    try {
      setIsLoadingTypeBreakdown(true);
      const queryString = buildQueryParams();
      const url = queryString 
        ? `${GetWorkflowTypeBreakdown}?${queryString}`
        : GetWorkflowTypeBreakdown;
      
      console.log('Fetching Workflow Type Breakdown from:', url);
      const response = await instance.get(url);
      
      console.log('Workflow Type Breakdown Response:', response);
      
      if (response && response.status === 200 && response.data) {
        // Handle both direct array and nested data structure
        const data = Array.isArray(response.data) ? response.data : (response.data.data || []);
        console.log('Workflow Type Breakdown Data:', data);
        if (Array.isArray(data)) {
          setWorkflowTypeBreakdownData(data);
          
          // Extract workflow names from breakdown data as fallback
          const workflowNames = data
            .map(item => item.name || item.workflowType || item.workflow_name)
            .filter(name => name && typeof name === 'string')
            .filter((name, index, self) => self.indexOf(name) === index); // Remove duplicates
          
          // Update workflow list, keeping existing ones and adding new ones
          if (workflowNames.length > 0) {
            setWorkflowList(prev => {
              const combined = [...prev, ...workflowNames];
              return combined.filter((name, index, self) => self.indexOf(name) === index).sort();
            });
          }
        } else {
          console.error('Workflow Type Breakdown data is not an array:', data);
          setWorkflowTypeBreakdownData([]);
        }
      } else {
        console.error('Error fetching workflow type breakdown - Invalid response:', response);
        setWorkflowTypeBreakdownData([]);
      }
    } catch (error) {
      console.error('Error fetching workflow type breakdown:', error);
      console.error('Error details:', error.response?.data || error.message);
      setWorkflowTypeBreakdownData([]);
    } finally {
      setIsLoadingTypeBreakdown(false);
    }
  };

  // Fetch workflow overview data
  const fetchWorkflowOverview = async () => {
    try {
      setIsLoadingOverview(true);
      const response = await instance.get(GetWorkflowOverview);
      
      if (response && response.status === 200 && response.data && Array.isArray(response.data)) {
        setWorkflowOverviewData(response.data);
        
        // Extract workflow names from overview data as fallback
        const workflowNames = response.data
          .map(item => item.workflowType)
          .filter(name => name && typeof name === 'string')
          .filter((name, index, self) => self.indexOf(name) === index); // Remove duplicates
        
        // Update workflow list, keeping existing ones and adding new ones
        if (workflowNames.length > 0) {
          setWorkflowList(prev => {
            const combined = [...prev, ...workflowNames];
            return combined.filter((name, index, self) => self.indexOf(name) === index).sort();
          });
        }
      } else {
        console.error('Error fetching workflow overview:', response);
        setWorkflowOverviewData([]);
      }
    } catch (error) {
      console.error('Error fetching workflow overview:', error);
      setWorkflowOverviewData([]);
    } finally {
      setIsLoadingOverview(false);
    }
  };

  // Fetch team activity data
  const fetchTeamActivity = async () => {
    try {
      setIsLoadingTeamActivity(true);
      const queryString = buildQueryParams();
      const url = queryString 
        ? `${GetTeamActivity}?${queryString}`
        : GetTeamActivity;
      const response = await instance.get(url);
      
      if (response && response.status === 200 && response.data && Array.isArray(response.data)) {
        setTeamActivityData(response.data);
        
        // Extract user names from team activity data as fallback
        const userNames = response.data
          .map(item => item.teamMember)
          .filter(name => name && typeof name === 'string')
          .filter((name, index, self) => self.indexOf(name) === index); // Remove duplicates
        
        // Update user list, keeping existing ones and adding new ones
        if (userNames.length > 0) {
          setUserList(prev => {
            const combined = [...prev, ...userNames];
            return combined.filter((name, index, self) => self.indexOf(name) === index).sort();
          });
        }
      } else {
        console.error('Error fetching team activity:', response);
        setTeamActivityData([]);
      }
    } catch (error) {
      console.error('Error fetching team activity:', error);
      setTeamActivityData([]);
    } finally {
      setIsLoadingTeamActivity(false);
    }
  };

  // Fetch pending workflow details data
  const fetchPendingWorkflowDetails = async () => {
    try {
      setIsLoadingPendingDetails(true);
      const queryString = buildQueryParams();
      const url = queryString 
        ? `${GetPendingWorkflowDetails}?${queryString}`
        : GetPendingWorkflowDetails;
      
      const response = await instance.get(url);
      
      if (response && response.status === 200 && response.data && Array.isArray(response.data)) {
        setPendingWorkflowData(response.data);
        
        // Extract unique transaction IDs from the response
        const transactionIds = response.data
          .map(item => item.transactionId)
          .filter(id => id !== null && id !== undefined)
          .map(id => String(id)) // Convert to string
          .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates
        
        // Update transaction ID list, keeping existing ones and adding new ones
        setTransactionIdList(prev => {
          const combined = [...prev, ...transactionIds];
          return combined.filter((id, index, self) => self.indexOf(id) === index).sort();
        });
      } else {
        console.error('Error fetching pending workflow details:', response);
        setPendingWorkflowData([]);
      }
    } catch (error) {
      console.error('Error fetching pending workflow details:', error);
      setPendingWorkflowData([]);
    } finally {
      setIsLoadingPendingDetails(false);
    }
  };
  
  // Fetch all unique transaction IDs
  const fetchTransactionIds = async () => {
    try {
      // Fetch all pending workflow details without filters to get all transaction IDs
      const response = await instance.get(GetPendingWorkflowDetails);
      
      if (response && response.status === 200 && response.data && Array.isArray(response.data)) {
        const transactionIds = response.data
          .map(item => item.transactionId)
          .filter(id => id !== null && id !== undefined)
          .map(id => String(id)) // Convert to string
          .filter((id, index, self) => self.indexOf(id) === index) // Remove duplicates
          .sort();
        
        if (transactionIds.length > 0) {
          setTransactionIdList(transactionIds);
        }
      }
    } catch (error) {
      console.error('Error fetching transaction IDs:', error);
    }
  };

  // Fetch workflow list and user list on mount
  useEffect(() => {
    // Fetch workflow list from WorkFlow ViewSet
    instance.get('bankmanagement/workflow/')
      .then((response) => {
        console.log('Workflow list response:', response);
        if (response && response.status === 200 && response.data) {
          // Handle both array and paginated response
          const workflows = Array.isArray(response.data) 
            ? response.data 
            : (response.data.results || response.data.data || []);
          
          const workflowNames = workflows
            .map(wf => wf.workflow_name || wf.name || wf)
            .filter(name => name && typeof name === 'string')
            .filter((name, index, self) => self.indexOf(name) === index); // Remove duplicates
          
          console.log('Workflow names extracted:', workflowNames);
          if (workflowNames.length > 0) {
            setWorkflowList(workflowNames);
          }
        } else {
          console.error('Invalid workflow list response:', response);
        }
      })
      .catch((error) => {
        console.error('Error fetching workflow list:', error);
        console.error('Error details:', error.response?.data || error.message);
        // Don't set empty array, let fallback from workflow overview populate it
      });

    // Fetch user list
    instance.get(GetUsers)
      .then((response) => {
        console.log('User list response:', response);
        if (response && response.status === 200 && response.data) {
          // Handle both array and paginated response
          const users = Array.isArray(response.data) 
            ? response.data 
            : (response.data.results || response.data.data || []);
          
          const filteredUsers = users.filter(
            (user) => user.status === "Active"
          );
          
          const userNames = filteredUsers
            .map(user => user.user_name || user.email || user)
            .filter(name => name && typeof name === 'string')
            .filter((name, index, self) => self.indexOf(name) === index); // Remove duplicates
          
          console.log('User names extracted:', userNames);
          if (userNames.length > 0) {
            setUserList(userNames);
          }
        } else {
          console.error('Invalid user list response:', response);
        }
      })
      .catch((error) => {
        console.error('Error fetching user list:', error);
        console.error('Error details:', error.response?.data || error.message);
        // Don't set empty array, let fallback from team activity populate it
      });

    // Fetch transaction IDs
    fetchTransactionIds();
  }, []);

  // Fetch data on component mount only (not on filter changes)
  useEffect(() => {
    fetchWorkflowDashboardSummary();
    fetchAgingAnalysis();
    fetchWorkflowTypeBreakdown();
    fetchWorkflowOverview();
    fetchTeamActivity();
    fetchPendingWorkflowDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only fetch on mount

  // Chart data based on workflow metrics
  const workflowStatusChartData = {
    datasets: [{
      data: [
        { title: "In Process", amount: inProcess, count: inProcess },
        { title: "Completed", amount: completed, count: completed },
        { title: "Rejected / Closed", amount: rejected, count: rejected }
      ]
    }]
  };

  const workflowStatusColors = [
    primaryBlueColor, 
    "rgba(22, 114, 222, 0.7)", 
    "rgba(22, 114, 222, 0.5)"
  ];

  // Workflow Type Breakdown data state
  const [workflowTypeBreakdownData, setWorkflowTypeBreakdownData] = useState([]);
  
  const maxWorkflowTypeValue = Math.max(...workflowTypeBreakdownData.map(item => item.value), 1);

  // Aging Analysis data state
  const [agingAnalysisData, setAgingAnalysisData] = useState({
    labels: [],
    datasets: [
      {
        label: "Count",
        data: [],
        backgroundColor: primaryBlueColor,
        borderColor: primaryBlueColor,
        borderWidth: 1,
      },
    ],
  });

  const agingAnalysisOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 5,
          color: "#333",
          font: {
            size: 11,
          },
        },
        grid: {
          color: "#e0e0e0",
        },
      },
      x: {
        ticks: {
          color: "#333",
          font: {
            size: 11,
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  // Workflow Overview data (Table 1)
  const [workflowOverviewData, setWorkflowOverviewData] = useState([]);

  // Team Activity data
  const [teamActivityData, setTeamActivityData] = useState([]);

  // Pending Workflow Details data (Table 2)
  const [pendingWorkflowData, setPendingWorkflowData] = useState([]);

  // Status options (you can replace with actual options from API)
  const statusOptions = [
    "Pending",
    "In Process",
    "In Progress",
    "Completed",
    "Rejected",
    "Cancelled"
  ];

  const Container = {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: 0,
    height: "calc(100vh - 180px)",
    maxHeight: "calc(100vh - 180px)",
    overflow: "hidden",
  };

  const handleApplyFilters = () => {
    // Fetch all data with applied filters
    fetchWorkflowDashboardSummary();
    fetchAgingAnalysis();
    fetchWorkflowTypeBreakdown();
    fetchWorkflowOverview();
    fetchTeamActivity();
    fetchPendingWorkflowDetails();
  };

  // Handlers for checkbox filters - Single selection enforced
  const handleWorkflowCheckboxChange = (workflowName) => {
    setSelectedWorkflows((prev) => {
      if (prev.includes(workflowName)) {
        return prev.filter((w) => w !== workflowName);
      } else {
        return [workflowName];
      }
    });
  };

  const handleUserCheckboxChange = (userName) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userName)) {
        return prev.filter((u) => u !== userName);
      } else {
        return [userName];
      }
    });
  };

  const handleTransactionIdCheckboxChange = (transactionId) => {
    setSelectedTransactionIds((prev) => {
      if (prev.includes(transactionId)) {
        return prev.filter((t) => t !== transactionId);
      } else {
        return [transactionId];
      }
    });
  };

  const handleClearWorkflowSearch = () => {
    setWorkflowNameSearch("");
    setSelectedWorkflows([]);
    setWorkflowName("");
  };

  const handleClearUserSearch = () => {
    setInitiatedBySearch("");
    setSelectedUsers([]);
    setInitiatedBy("");
  };

  const handleClearTransactionIdSearch = () => {
    setTransactionIdSearch("");
    setSelectedTransactionIds([]);
    setTransactionId("");
  };

  const handleClearFilters = () => {
    // Clear all filter values
    setWorkflowName("");
    setDateReceivedFrom("");
    setDateReceivedTo("");
    setInitiatedBy("");
    setStatus("");
    setTransactionId("");
    setSelectedWorkflows([]);
    setSelectedUsers([]);
    setSelectedTransactionIds([]);
    setWorkflowNameSearch("");
    setInitiatedBySearch("");
    setTransactionIdSearch("");
    // Refetch data with cleared filters
    fetchWorkflowDashboardSummary();
    fetchAgingAnalysis();
    fetchWorkflowTypeBreakdown();
    fetchWorkflowOverview();
    fetchTeamActivity();
    fetchPendingWorkflowDetails();
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  const isFilterBtnDisable = () => {
    // Return true if filters are disabled, false otherwise
    return !workflowName && !selectedWorkflows.length && !dateReceivedFrom && !dateReceivedTo && !initiatedBy && !selectedUsers.length && !status && !transactionId && !selectedTransactionIds.length;
  };

  return (
    <div style={Container}>
    
      {/* Filter Sidebar */}
      <CommonFilterSidebar
        isOpen={filterSidebarOpen}
        filterConfigs={[
          {
            type: "workflowName",
            label: "Workflow Name",
            placeholder: "Search workflows",
            searchValue: workflowNameSearch,
            onSearchChange: setWorkflowNameSearch,
            items: workflowList,
            selectedItems: selectedWorkflows,
            onItemChange: handleWorkflowCheckboxChange,
            onClear: handleClearWorkflowSearch,
            singleSelection: true,
          },
          // {
          //   type: "workflowName",
          //   label: "Transaction ID",
          //   placeholder: "Search transaction IDs",
          //   searchValue: transactionIdSearch,
          //   onSearchChange: setTransactionIdSearch,
          //   items: transactionIdList,
          //   selectedItems: selectedTransactionIds,
          //   onItemChange: handleTransactionIdCheckboxChange,
          //   onClear: handleClearTransactionIdSearch,
          //   singleSelection: true,
          // },
          {
            type: "dateRange",
            label: "Date Received",
            startDate: dateReceivedFrom,
            setStartDate: setDateReceivedFrom,
            endDate: dateReceivedTo,
            setEndDate: setDateReceivedTo,
            maxDate: new Date(),
            onKeyUp: handleEnter,
          },
          {
            type: "initiatedBy",
            label: "Initiated By",
            placeholder: "Search users",
            searchValue: initiatedBySearch,
            onSearchChange: setInitiatedBySearch,
            items: userList,
            selectedItems: selectedUsers,
            onItemChange: handleUserCheckboxChange,
            onClear: handleClearUserSearch,
            singleSelection: true,
          },
          {
            type: "custom",
            content: (
              <>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#000",
                    marginBottom: "12px",
                  }}
                >
                  Status
                </Typography>
                <Box onKeyUp={handleEnter}>
                  <CommonSelect
                    handleChange={(event, value, reason) => {
                      if (reason === "clear") {
                        setStatus("");
                      } else {
                        setStatus(value || "");
                      }
                    }}
                    options={statusOptions}
                    placeholder="Select status"
                    value={status || ""}
                  />
                </Box>
              </>
            ),
          },
        ]}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        isLoading={isLoadingSummary || isLoadingAgingAnalysis || isLoadingTypeBreakdown || isLoadingOverview || isLoadingTeamActivity || isLoadingPendingDetails}
        isFilterDisabled={isFilterBtnDisable()}
      />

      {/* Main Content Area */}
      <Box className="main-content-area">
        <Box className="page-header-container">
          <Typography
            variant="h5"
            className="page-heading"
          >
           Email Monitoring Dashboard
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            
            <IconButton
              onClick={() => setFilterSidebarOpen(!filterSidebarOpen)}
              className="filter-toggle-button"
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Summary Cards Section - 10% height */}
        <Box
          sx={{
            height: "10%",
            minHeight: "70px",
            maxHeight: "90px",
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          {/* Total Workflows Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              position: "relative",
            }}
          >
            {isLoadingSummary ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                <CircularProgress size={24} sx={{ color: primaryBlueColor }} />
              </Box>
            ) : (
              <>
                <FaFolder
                  size={24}
                  color={primaryBlueColor}
                />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: primaryBlueColor,
                      fontWeight: "600",
                      marginBottom: "2px",
                      lineHeight: 1.2,
                    }}
                  >
                    Total Workflows
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: "700",
                      color: "#333",
                      lineHeight: 1.2,
                    }}
                  >
                    {totalWorkflows}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* In Process Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              position: "relative",
            }}
          >
            {isLoadingSummary ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                <CircularProgress size={24} sx={{ color: primaryBlueColor }} />
              </Box>
            ) : (
              <>
                <FaCog
                  size={24}
                  color={primaryBlueColor}
                />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: primaryBlueColor,
                      fontWeight: "600",
                      marginBottom: "2px",
                      lineHeight: 1.2,
                    }}
                  >
                    In Process
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: "700",
                      color: "#333",
                      lineHeight: 1.2,
                    }}
                  >
                    {inProcess}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Completed Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              position: "relative",
            }}
          >
            {isLoadingSummary ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                <CircularProgress size={24} sx={{ color: primaryBlueColor }} />
              </Box>
            ) : (
              <>
                <FaCheckSquare
                  size={24}
                  color={primaryBlueColor}
                />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: primaryBlueColor,
                      fontWeight: "600",
                      marginBottom: "2px",
                      lineHeight: 1.2,
                    }}
                  >
                    Completed
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: "700",
                      color: "#333",
                      lineHeight: 1.2,
                    }}
                  >
                    {completed}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Rejected Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              position: "relative",
            }}
          >
            {isLoadingSummary ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                <CircularProgress size={24} sx={{ color: primaryBlueColor }} />
              </Box>
            ) : (
              <>
                <FaTimesCircle
                  size={24}
                  color={primaryBlueColor}
                />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: primaryBlueColor,
                      fontWeight: "600",
                      marginBottom: "2px",
                      lineHeight: 1.2,
                    }}
                  >
                    Rejected
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: "700",
                      color: "#333",
                      lineHeight: 1.2,
                    }}
                  >
                    {rejected}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Overdue Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              position: "relative",
            }}
          >
            {isLoadingSummary ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                <CircularProgress size={24} sx={{ color: primaryBlueColor }} />
              </Box>
            ) : (
              <>
                <FaExclamationTriangle
                  size={24}
                  color={primaryBlueColor}
                />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: primaryBlueColor,
                      fontWeight: "600",
                      marginBottom: "2px",
                      lineHeight: 1.2,
                    }}
                  >
                    Overdue
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: "700",
                      color: "#333",
                      lineHeight: 1.2,
                    }}
                  >
                    {overdue}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Charts Section - 25% height */}
        <Box
          sx={{
            height: "35%",
            minHeight: "160px",
            maxHeight: "200px",
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          {/* Workflow Status Distribution - Donut Chart */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                color: primaryBlueColor,
                marginBottom: "8px",
              }}
            >
              Workflow Status Distribution
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 0,
                overflow: "hidden",
                padding: "4px 0",
                width: "100%",
                gap: "16px",
                position: "relative",
              }}
            >
              {isLoadingSummary ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                  <CircularProgress size={40} sx={{ color: primaryBlueColor }} />
                </Box>
              ) : (
                <>
                  {/* Legend on the left */}
                  <Box 
                    sx={{ 
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      minWidth: "120px",
                      maxWidth: "120px",
                      flexShrink: 0,
                    }}
                  >
                    {workflowStatusChartData.datasets[0].data.map((item, index) => (
                  <Box 
                    key={item.title}
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "6px" 
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: "10px", 
                        height: "10px", 
                        backgroundColor: workflowStatusColors[index], 
                        borderRadius: "2px",
                        flexShrink: 0,
                      }} 
                    />
                    <Typography 
                      sx={{ 
                        fontSize: "13px", 
                        color: "#333",
                        whiteSpace: "nowrap",
                        fontWeight: "500",
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Chart on the right */}
              <Box 
                sx={{ 
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 0,
                  overflow: "hidden",
                  width: "120px",
                  flexShrink: 0,
                  position: "relative",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: "120px",
                    height: "120px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <style>
                    {`
                      #workflow-status-distribution {
                        width: 120px !important;
                        max-width: 120px !important;
                        height: 120px !important;
                        max-height: 120px !important;
                        min-height: 120px !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        box-sizing: border-box !important;
                        overflow: hidden !important;
                      }
                      #workflow-status-distribution + div {
                        display: none !important;
                      }
                      #workflow-status-distribution > div {
                        width: 120px !important;
                        height: 120px !important;
                        max-width: 120px !important;
                        max-height: 120px !important;
                        box-sizing: border-box !important;
                        overflow: hidden !important;
                      }
                      #workflow-status-distribution svg,
                      #workflow-status-distribution canvas {
                        width: 120px !important;
                        height: 120px !important;
                        max-width: 120px !important;
                        max-height: 120px !important;
                        box-sizing: border-box !important;
                      }
                      #workflow-status-distribution svg path {
                        stroke: none !important;
                        stroke-width: 0 !important;
                      }
                      #workflow-status-distribution svg circle {
                        stroke: none !important;
                        stroke-width: 0 !important;
                      }
                    `}
                  </style>
                  <GenericDonutChart
                    data={workflowStatusChartData}
                    colorsList={workflowStatusColors}
                    title="workflow-status-distribution"
                    percentageInnerDonut={`${totalWorkflows}`}
                    displayDonutInnerText={true}
                    dataCy="workflow-status-chart"
                    innerRadiusPercent={25}
                    showCountOnly={true}
                  />
                </Box>
              </Box>
                </>
              )}
            </Box>
          </Box>

          {/* Aging Analysis Bar Chart */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: primaryBlueColor,
                marginBottom: "8px",
              }}
            >
              Aging Analysis
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 0,
                padding: "4px 0",
                position: "relative",
              }}
            >
              {isLoadingAgingAnalysis ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                  <CircularProgress size={40} sx={{ color: primaryBlueColor }} />
                </Box>
              ) : (
                /* Bar chart using react-chartjs-2 */
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: "120px",
                    position: "relative",
                  }}
                >
                  <Bar 
                    data={agingAnalysisData} 
                    options={agingAnalysisOptions}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Workflow Type Breakdown */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: primaryBlueColor,
                marginBottom: "8px",
              }}
            >
              Request Type Breakdown
            </Typography>
            <Box 
              sx={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "10px", 
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                minHeight: 0,
                paddingRight: "12px",
                position: "relative",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#a8a8a8",
                  },
                },
              }}
            >
              {isLoadingTypeBreakdown ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", minHeight: "120px" }}>
                  <CircularProgress size={40} sx={{ color: primaryBlueColor }} />
                </Box>
              ) : (
                workflowTypeBreakdownData.map((item, index) => {
                const percentage = maxWorkflowTypeValue > 0 ? (item.value / maxWorkflowTypeValue) * 100 : 0;
                return (
                  <Box
                    key={item.name}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "2px",
                      }}
                    >
                      <Typography 
                        sx={{ 
                          fontSize: "12px", 
                          color: "#333", 
                          fontWeight: "500",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#333", flexShrink: 0, marginLeft: "12px", minWidth: "30px", textAlign: "right" }}>
                        {item.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: "100%",
                        height: "20px",
                        backgroundColor: "#e0e0e0",
                        borderRadius: "4px",
                        overflow: "hidden",
                        position: "relative",
                        padding: "2px",
                        boxSizing: "border-box",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${percentage}%`,
                          height: "100%",
                          backgroundColor: primaryBlueColor,
                          borderRadius: "3px",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                  </Box>
                );
              })
              )}
            </Box>
          </Box>
        </Box>

        {/* Tables Section - 65% height, split layout */}
        <Box
          sx={{
            height: "55%",
            minHeight: "400px",
            maxHeight: "600px",
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          {/* Table 1 - Workflow Overview - 50% width, full height */}
          <Box
            sx={{
              width: "50%",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: primaryBlueColor,
                marginBottom: "8px",
              }}
            >
              Workflow Overview
            </Typography>
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: "none",
                flex: 1,
                overflow: "auto",
                position: "relative",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "10px",
                },
              }}
            >
              {isLoadingOverview ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", minHeight: "200px" }}>
                  <CircularProgress size={40} sx={{ color: primaryBlueColor }} />
                </Box>
              ) : (
                <Table size="small" sx={{ borderCollapse: "separate" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#f5f5f5",
                          color: primaryBlueColor,
                          fontWeight: "600",
                          fontSize: "12px",
                          padding: "8px 12px",
                          borderBottom: `1px solid ${primaryBlueColor}`,
                        }}
                      >
                        Workflow Type
                      </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      In Progress
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Overdue
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Avg. Age
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workflowOverviewData.map((row, index) => (
                    <TableRow
                      key={row.workflowType}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(22, 114, 222, 0.04)",
                        },
                        borderBottom: index < workflowOverviewData.length - 1 ? "1px solid #e0e0e0" : "none",
                      }}
                    >
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.workflowType}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "8px 12px",
                        }}
                      >
                        <Tooltip
                          title={
                            <Box sx={{ fontSize: "12px", lineHeight: "1.6" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                                <Box sx={{ width: "12px", height: "12px", backgroundColor: "#4caf50", borderRadius: "2px" }} />
                                <Box>Completed: {row.inProgress.completedCount || 0}</Box>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                                <Box sx={{ width: "12px", height: "12px", backgroundColor: primaryBlueColor, borderRadius: "2px" }} />
                                <Box>In Process: {row.inProgress.inProcessCount || 0}</Box>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                                <Box sx={{ width: "12px", height: "12px", backgroundColor: "#FFC107", borderRadius: "2px" }} />
                                <Box>Rejected: {row.inProgress.rejectedCount || 0}</Box>
                              </Box>
                              <Box sx={{ marginTop: "6px", paddingTop: "6px", borderTop: "1px solid rgba(255,255,255,0.3)", fontWeight: "600" }}>
                                Total: {row.inProgress.totalCount || 0}
                              </Box>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <Box sx={{ display: "flex", alignItems: "center", width: "100px", height: "16px", backgroundColor: "#e0e0e0", borderRadius: "4px", overflow: "hidden", cursor: "pointer", position: "relative" }}>
                            {/* Completed - Green (leftmost) */}
                            <Box 
                              sx={{ 
                                width: `${row.inProgress.completed || 0}%`, 
                                height: "100%", 
                                backgroundColor: "#4caf50",
                                transition: "all 0.3s ease",
                                minWidth: row.inProgress.completed > 0 ? "2px" : "0px",
                              }} 
                            />
                            {/* In Process - Blue (middle) */}
                            <Box 
                              sx={{ 
                                width: `${row.inProgress.remaining || 0}%`, 
                                height: "100%", 
                                backgroundColor: primaryBlueColor,
                                transition: "all 0.3s ease",
                                minWidth: row.inProgress.remaining > 0 ? "2px" : "0px",
                              }} 
                            />
                            {/* Rejected - Amber (rightmost) */}
                            <Box 
                              sx={{ 
                                width: `${row.inProgress.rejected || 0}%`, 
                                height: "100%", 
                                backgroundColor: "#FFC107",
                                transition: "all 0.3s ease",
                                minWidth: row.inProgress.rejected > 0 ? "2px" : "0px",
                              }} 
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                          fontWeight: "600",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <Box sx={{ width: "8px", height: "8px", backgroundColor: "#333", borderRadius: "2px" }} />
                          {row.overdue}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.avgAge}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "8px 12px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-block",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "600",
                            backgroundColor: 
                              row.status === "On Track" ? "rgba(76, 175, 80, 0.15)" : 
                              row.status === "At Risk" ? "rgba(255, 152, 0, 0.15)" : 
                              "rgba(244, 67, 54, 0.15)",
                            color: 
                              row.status === "On Track" ? "#4caf50" : 
                              row.status === "At Risk" ? "#ff9800" : 
                              "#f44336",
                          }}
                        >
                          {row.status === "On Track" ? "On Track" : row.status === "At Risk" ? "At Risk" : "Delayed"}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </TableContainer>
          </Box>

          {/* Right Side - 50% width, split vertically */}
          <Box
            sx={{
              width: "50%",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {/* Team Activity - Top 50% of right side (25% of container) */}
            <Box
              sx={{
                height: "50%",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                padding: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid #e0e0e0",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: primaryBlueColor,
                  marginBottom: "8px",
                }}
              >
                Team Activity
              </Typography>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  boxShadow: "none",
                  flex: 1,
                  overflow: "auto",
                  position: "relative",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "10px",
                  },
                }}
              >
                {isLoadingTeamActivity ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", minHeight: "200px" }}>
                    <CircularProgress size={40} sx={{ color: primaryBlueColor }} />
                  </Box>
                ) : (
                  <Table size="small" sx={{ borderCollapse: "separate" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: "#f5f5f5",
                            color: primaryBlueColor,
                            fontWeight: "600",
                            fontSize: "12px",
                            padding: "8px 12px",
                            borderBottom: `1px solid ${primaryBlueColor}`,
                          }}
                        >
                          Team Member
                        </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f5f5f5",
                          color: primaryBlueColor,
                          fontWeight: "600",
                          fontSize: "12px",
                          padding: "8px 12px",
                          borderBottom: `1px solid ${primaryBlueColor}`,
                          textAlign: "center",
                        }}
                      >
                        Tasks Assigned
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f5f5f5",
                          color: primaryBlueColor,
                          fontWeight: "600",
                          fontSize: "12px",
                          padding: "8px 12px",
                          borderBottom: `1px solid ${primaryBlueColor}`,
                          textAlign: "center",
                        }}
                      >
                        Tasks Overdue
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamActivityData.map((row, index) => (
                      <TableRow
                        key={row.teamMember}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(22, 114, 222, 0.04)",
                          },
                          borderBottom: index < teamActivityData.length - 1 ? "1px solid #e0e0e0" : "none",
                        }}
                      >
                        <TableCell
                          sx={{
                            color: "#333",
                            fontSize: "12px",
                            padding: "8px 12px",
                          }}
                        >
                          {row.teamMember}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#333",
                            fontSize: "12px",
                            padding: "8px 12px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          {row.tasksAssigned}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#333",
                            fontSize: "12px",
                            padding: "8px 12px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          {row.tasksOverdue}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                )}
              </TableContainer>
            </Box>

            {/* Pending Workflow Details - Bottom 50% of right side (25% of container) */}
            <Box
              sx={{
                height: "50%",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                padding: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid #e0e0e0",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: primaryBlueColor,
                  marginBottom: "8px",
                  flexShrink: 0,
                }}
              >
                Pending Workflow Details
              </Typography>
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: "none",
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                position: "relative",
                zIndex: 1,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "10px",
                },
              }}
            >
              {isLoadingPendingDetails ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", minHeight: "200px" }}>
                  <CircularProgress size={40} sx={{ color: primaryBlueColor }} />
                </Box>
              ) : (
                <Table size="small" sx={{ borderCollapse: "separate" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#f5f5f5",
                          color: primaryBlueColor,
                          fontWeight: "600",
                          fontSize: "12px",
                          padding: "8px 12px",
                          borderBottom: `1px solid ${primaryBlueColor}`,
                        }}
                      >
                        Transaction ID
                      </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Workflow Type
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Current Step
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Age
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingWorkflowData.map((row, index) => (
                    <TableRow
                      key={`${row.transactionId}-${index}`}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(22, 114, 222, 0.04)",
                        },
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.transactionId}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.workflowType}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.currentStep}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.age}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </TableContainer>
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default WorkflowDashboardContainer